import {
  type ActionReducerMapBuilder,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import { uploaderStateDeleted } from '@iconik-io/shared-lib/src/uploader/states_slice';

import {
  TRANSCODE_MEDIUM_PRIORITY,
  UPLOADER_PERMISSIONS_COLLECTION,
  UPLOADER_PERMISSIONS_DEFAULT,
} from './constants';
import type {
  UploaderAppState,
  UploaderCollectionPermissions,
  UploaderTopLevelPermissions,
} from './types';

const initialState: UploaderAppState = {
  isCollectionsCreation: false,
  selectedStorage: undefined,
  reflectDirectories: true,
  transcodePriority: TRANSCODE_MEDIUM_PRIORITY,
  collectionPermissions: UPLOADER_PERMISSIONS_COLLECTION,
  topLevelPermissions: UPLOADER_PERMISSIONS_DEFAULT,
  expanded: false,
};

export const uploaderSlice = createSlice({
  name: 'uploader',
  initialState,
  reducers: {
    uploaderSetCollectionsCreation: (state, action: PayloadAction<boolean>) => {
      state.isCollectionsCreation = action.payload;
    },
    uploaderSetStorage: (state, action: PayloadAction<string | undefined>) => {
      state.selectedStorage = action.payload;
    },
    uploaderSetReflectDirectories: (state, action: PayloadAction<boolean>) => {
      state.reflectDirectories = action.payload;
    },
    uploaderSetTranscodePriority: (state, action: PayloadAction<number>) => {
      state.transcodePriority = action.payload;
    },
    uploaderSetTopLevelPermissions: (
      state,
      action: PayloadAction<UploaderTopLevelPermissions>,
    ) => {
      state.topLevelPermissions = action.payload;
    },
    uploaderSetCollectionPermissions: (
      state,
      action: PayloadAction<UploaderCollectionPermissions>,
    ) => {
      state.collectionPermissions = action.payload;
    },
    uploaderSetExpanded: (state, action: PayloadAction<boolean>) => {
      state.expanded = action.payload;
    },
  },
});

export const {
  uploaderSetCollectionsCreation,
  uploaderSetStorage,
  uploaderSetReflectDirectories,
  uploaderSetTranscodePriority,
  uploaderSetTopLevelPermissions,
  uploaderSetCollectionPermissions,
  uploaderSetExpanded,
} = uploaderSlice.actions;

export const uploaderReducer = uploaderSlice.reducer;

/**
 * The uploader keeps several records keyed by upload state id next to the
 * states themselves. Each of those slices passes this as its `extraReducers` so
 * its entry goes away with the upload, rather than living until the tab is
 * closed.
 */
export const dropRemovedUploadEntries = <T>(
  builder: ActionReducerMapBuilder<Record<string, T>>,
) => {
  builder.addCase(uploaderStateDeleted, (state, action) => {
    delete state[action.payload];
  });
};
